import { Box, Container, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import img1 from "../../assets/images/clients/att-logo.png";
import img2 from "../../assets/images/clients/fobesoft.png";
import img3 from "../../assets/images/clients/CVS.png";
import img4 from "../../assets/images/clients/Toyota.png";
import img5 from "../../assets/images/clients/google.png";
import img6 from "../../assets/images/clients/verizon.svg";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const slideRight = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const logos = [img1, img2, img3, img4, img5, img6];

const stats = [
  { value: "50+", label: "Clients Worldwide" },
  { value: "10+",  label: "Years of Experience" },
  { value: "100%", label: "Client Satisfaction" },
  { value: "30+",  label: "Global Partners" },
];

const Clients = () => {
  return (
    <Box
      sx={{
        py: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(160deg, #f8faff 0%, #f0f4ff 50%, #f5f0ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.1) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Soft glow orbs */}
      <Box sx={{ position: "absolute", top: "-100px", left: "-100px", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.1) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "absolute", bottom: "-100px", right: "-100px", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.08) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8 } }}>
          {/* Pill */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.8,
              borderRadius: "999px",
              border: "1px solid rgba(91,124,250,0.3)",
              background: "rgba(91,124,250,0.06)",
              mb: 3,
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "#5b7cfa",
                  animation: `${pulseRing} 1.8s ease-out infinite`,
                },
              }}
            />
            <Typography sx={{ color: "#5b7cfa", fontSize: 12, fontWeight: 700, letterSpacing: 1.5 }}>
              TRUSTED PARTNERS
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "30px", sm: "40px", md: "52px" },
              fontWeight: 800,
              letterSpacing: "-1px",
              lineHeight: 1.15,
              mb: 2.5,
              animation: `${fadeUp} 0.7s ease-out 0.1s both`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #5b7cfa 50%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Collaborators
          </Typography>

          {/* Animated underline */}
          <Box
            sx={{
              width: 80,
              height: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, #5b7cfa, #764ba2)",
              mx: "auto",
              mb: 3,
              transformOrigin: "left",
              animation: `${slideRight} 0.8s ease-out 0.3s both`,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "15px", sm: "17px", md: "18px" },
              color: "#64748b",
              maxWidth: 640,
              mx: "auto",
              lineHeight: 1.8,
              animation: `${fadeUp} 0.7s ease-out 0.2s both`,
            }}
          >
            We take pride in supporting clients of all sizes — from global enterprises to fast-growing startups. Delivering tailored solutions built on trust and mutual success.
          </Typography>
        </Box>

        {/* Stats row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
            gap: { xs: 2, sm: 3 },
            mb: { xs: 7, sm: 9 },
          }}
        >
          {stats.map((stat, i) => (
            <Box
              key={i}
              sx={{
                textAlign: "center",
                p: { xs: "20px 16px", sm: "24px 20px" },
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: "18px",
                boxShadow: "0 2px 12px rgba(91,124,250,0.06)",
                animation: `${fadeUp} 0.6s ease-out ${0.1 * i + 0.3}s both`,
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "1px solid rgba(91,124,250,0.3)",
                  boxShadow: "0 8px 28px rgba(91,124,250,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "28px", sm: "36px" },
                  fontWeight: 900,
                  background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  lineHeight: 1.1,
                  mb: 0.5,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  color: "#94a3b8",
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Trusted by label */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            color: "#94a3b8",
            mb: 4,
            textTransform: "uppercase",
            animation: `${fadeUp} 0.6s ease-out 0.5s both`,
          }}
        >
          Trusted by world-class brands
        </Typography>
      </Container>

      {/* Single marquee row */}
      <Box sx={{ position: "relative", overflow: "hidden", py: "12px" }}>
        {/* Fade edges — match light bg */}
        <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 120, background: "linear-gradient(90deg, #f8faff 0%, transparent 100%)", zIndex: 10, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 120, background: "linear-gradient(270deg, #f5f0ff 0%, transparent 100%)", zIndex: 10, pointerEvents: "none" }} />

        <Box
          sx={{
            display: "flex",
            gap: "24px",
            width: "max-content",
            animation: `${marquee} 25s linear infinite`,
          }}
        >
          {[...logos, ...logos, ...logos, ...logos].map((img, i) => (
            <Box
              key={i}
              sx={{
                flex: "0 0 auto",
                width: 140,
                height: 76,
                borderRadius: "14px",
                border: "1px solid #e8edf5",
                background: "#fff",
                boxShadow: "0 2px 10px rgba(91,124,250,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "1px solid rgba(91,124,250,0.4)",
                  boxShadow: "0 12px 28px rgba(91,124,250,0.15)",
                  transform: "translateY(-6px)",
                },
                "& img": {
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  filter: "grayscale(100%)",
                  opacity: 0.55,
                  transition: "all 0.3s ease",
                },
                "&:hover img": {
                  filter: "grayscale(0%)",
                  opacity: 1,
                },
              }}
            >
              <img src={img} alt={`Client ${(i % logos.length) + 1}`} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Clients;
